/**
 * Gaia chat app — embedded as an iframe on Gorgias helpdesk pages.
 * State-based views: chat, conversations, documents, settings.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { InboxIcon, Maximize2, Minimize2, PanelRightOpen, SquarePen, X } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useAssistantRuntime, useThread, useThreadList } from '@assistant-ui/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/client/components/ui/tooltip';
import {
  RuntimeProvider,
  useSetThreadCreationMetadata,
  useUserRole,
} from '@/client/app/providers/RuntimeProvider';
import { ConversationsView } from '@/client/app/components/ConversationsView';
import { DocumentsView } from '@/client/app/components/DocumentsView';
import { MemoryView } from '@/client/app/components/MemoryView';
import { ScheduledRunsView } from '@/client/app/components/ScheduledRunsView';
import { SettingsView } from '@/client/app/components/SettingsView';
import { ErrorBoundary } from '@/client/components/ErrorBoundary';
import { Thread } from '@/client/components/assistant-ui/thread';
import {
  ThreadSelector,
  type ThreadSelectorSelection,
} from '@/client/components/assistant-ui/thread-selector';
import { SignInScreen } from '@/client/app/components/SignInScreen';
import { UserNameProvider } from '@/client/lib/userNameContext';
import { useAuth } from '@/client/app/providers/auth-context';
import { getThread, listThreadsWithDates } from '@/client/lib/chatApi';
import { isBackgroundThreadMetadata } from '@/client/lib/backgroundThreads';
import { NavigationContext } from '@/client/app/providers/navigation-context';
import { useExternalAssets } from '@/client/lib/assetsContext';
import { useFiles } from '@/client/lib/filesContext';
import { AssetPanel } from '@/client/app/components/AssetPanel';
import { InboxView } from '@/client/app/components/InboxView';

type AppView = 'chat' | 'home' | 'inbox' | 'settings' | 'documents' | 'scheduledRuns' | 'memory';

/** Derive a per-account (and per-agent) localStorage key so threads don't leak. */
function threadStorageKey(pageUrl: string, agentSlug?: string): string {
  try {
    const hostname = new URL(pageUrl).hostname; // e.g. "acme.gorgias.com"
    const subdomain = hostname.split('.')[0];
    const base = `gaia-embed-thread-id:${subdomain}`;
    return agentSlug ? `${base}:${agentSlug}` : base;
  } catch {
    return agentSlug ? `gaia-embed-thread-id:${agentSlug}` : 'gaia-embed-thread-id';
  }
}

function getStoredThreadId(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storeThreadId(key: string, id: string | null) {
  try {
    if (id) {
      localStorage.setItem(key, id);
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    /* private browsing */
  }
}

type EmbedWidgetState = 'closed' | 'sidebar' | 'floating' | 'fullscreen';

function AppHeader({
  view,
  onViewChange,
  onThreadSelect,
}: {
  view: AppView;
  onViewChange: (v: AppView) => void;
  onThreadSelect: (selection: ThreadSelectorSelection) => void;
}) {
  const runtime = useAssistantRuntime();
  const {
    pageUrl,
    userName,
    userEmail,
    googleUser,
    signInWithGoogle,
    signOutFromGoogle,
  } = useAuth();
  const userRole = useUserRole();
  const storageKey = threadStorageKey(pageUrl);
  const didInit = useRef(false);
  const mainThreadId = useThreadList((s) => s.mainThreadId);
  const threadItems = useThreadList((s) => s.threadItems);

  // Inbox count — uses react-query with 30s polling
  const { data: inboxCount = 0 } = useQuery({
    queryKey: ['inbox-count'],
    queryFn: async () => {
      const { totalCount } = await listThreadsWithDates({ limit: 1, offset: 0, status: 'interrupted' });
      return totalCount;
    },
    refetchInterval: 30_000,
  });

  // On mount: immediately create a new thread so the UI is settled before
  // the first paint, then restore the persisted thread (if any) once verified.
  useLayoutEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    // Always start with a clean new thread — guarantees ThreadWelcome renders
    // on the very first frame (no layout shift).
    runtime.threads.switchToNewThread();

    const storedId = getStoredThreadId(storageKey);
    if (storedId) {
      // Restore only normal chat threads. Background inbox threads stay in inbox.
      getThread(storedId)
        .then((thread) => {
          const metadata =
            thread.metadata && typeof thread.metadata === 'object' && !Array.isArray(thread.metadata)
              ? (thread.metadata as Record<string, unknown>)
              : null;
          if (isBackgroundThreadMetadata(metadata)) {
            storeThreadId(storageKey, null);
            return;
          }

          runtime.threads.switchToThread(storedId);
        })
        .catch(() => {
          storeThreadId(storageKey, null);
        });
    }
  }, [runtime, storageKey]);

  // Track thread changes and persist the remoteId
  useEffect(() => {
    const item = threadItems[mainThreadId];
    const remoteId = item?.remoteId;
    storeThreadId(storageKey, remoteId ?? null);
  }, [mainThreadId, threadItems, storageKey]);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  const handleClose = useCallback(() => {
    window.parent.postMessage({ type: 'GAIA_EMBED_CLOSE' }, '*');
  }, []);

  const handleNewThread = useCallback(() => {
    runtime.threads.switchToNewThread();
    onViewChange('chat');
  }, [runtime, onViewChange]);

  // Track the current embed layout state so the fullscreen toggle can show
  // the right icon. Updated via GAIA_EMBED_STATE messages from embed.js.
  const [widgetState, setWidgetState] = useState<EmbedWidgetState>('sidebar');
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const data = event.data;
      if (data && data.type === 'GAIA_EMBED_STATE' && typeof data.state === 'string') {
        setWidgetState(data.state as EmbedWidgetState);
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const isFullscreen = widgetState === 'fullscreen';
  const handleToggleFullscreen = useCallback(() => {
    window.parent.postMessage(
      { type: isFullscreen ? 'GAIA_EMBED_COLLAPSE' : 'GAIA_EMBED_EXPAND' },
      '*',
    );
  }, [isFullscreen]);

  return (
    <>
      <ThreadSelector
        className=""
        activeView={view}
        onHomeClick={() => onViewChange('home')}
        onInboxClick={() => onViewChange('inbox')}
        maxThreads={10}
        onDocumentsClick={() => onViewChange('documents')}
        onScheduledRunsClick={() => onViewChange('scheduledRuns')}
        onMemoryClick={() => onViewChange('memory')}
        onSettingsClick={() => onViewChange('settings')}
        onThreadSelect={onThreadSelect}
        embedUser={{
          name: googleUser?.name ?? userName,
          email: googleUser?.email ?? userEmail,
          picture: googleUser?.picture,
          role: userRole ?? undefined,
        }}
        onSignOut={googleUser && signOutFromGoogle ? signOutFromGoogle : undefined}
        onSignIn={!googleUser && signInWithGoogle ? signInWithGoogle : undefined}
        onRefresh={handleRefresh}
        actions={
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleNewThread}
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="New chat"
                >
                  <SquarePen className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">New chat</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onViewChange('inbox')}
                  className="relative flex h-7 w-7 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Inbox"
                >
                  <InboxIcon className="h-4 w-4" />
                  {inboxCount > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">
                      {inboxCount > 9 ? '9+' : inboxCount}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Inbox</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleToggleFullscreen}
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={isFullscreen ? 'Exit full width' : 'Go full width'}
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                {isFullscreen ? 'Exit full width' : 'Go full width'}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleClose}
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Close</TooltipContent>
            </Tooltip>
          </>
        }
      />
    </>
  );
}

function AppContent() {
  const [visibility, setVisibility] = useState<'public' | 'private'>('private');
  const [view, setView] = useState<AppView>('chat');
  const [documentPath, setDocumentPath] = useState<string | null>(null);
  const runtime = useAssistantRuntime();
  const { userEmail, googleUser, googleSessionLoading } = useAuth();
  const setThreadCreationMetadata = useSetThreadCreationMetadata();
  const { panelCollapsed, setPanelCollapsed, setPanelTab, setHasSidebarPanel } = useExternalAssets();
  const { files: fsFiles } = useFiles();
  const showAssetPanel = !panelCollapsed && view === 'chat';

  // Register that a sidebar panel is available so AssetWidget shows the "View in panel" button
  useEffect(() => {
    setHasSidebarPanel(true);
    return () => setHasSidebarPanel(false);
  }, [setHasSidebarPanel]);

  // Close asset panel when switching away from chat
  useEffect(() => {
    if (view !== 'chat') {
      setPanelCollapsed(true);
    }
  }, [view, setPanelCollapsed]);

  // Auto-open panel when shared files first appear
  const hasSharedFiles = useMemo(
    () => Object.keys(fsFiles).some((p) => p.startsWith('/shared/')),
    [fsFiles],
  );
  const prevHasSharedFiles = useRef(false);
  useEffect(() => {
    if (hasSharedFiles && !prevHasSharedFiles.current && view === 'chat') {
      setPanelCollapsed(false);
      setPanelTab('files');
    }
    prevHasSharedFiles.current = hasSharedFiles;
  }, [hasSharedFiles, view, setPanelCollapsed, setPanelTab]);

  const navigateToDocument = useCallback((objectPath: string) => {
    setDocumentPath(objectPath);
    setView('documents');
  }, []);

  const isSupportUser = userEmail === 'support@gorgias.com';
  const needsSignIn = isSupportUser && !googleUser && !googleSessionLoading;

  // Keep thread-creation metadata in sync with visibility and source
  useEffect(() => {
    setThreadCreationMetadata({
      visibility,
    });
  }, [visibility, setThreadCreationMetadata]);

  // Notify parent when agent finishes streaming
  const isRunning = useThread((t) => t.isRunning);
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (wasRunningRef.current && !isRunning) {
      window.parent.postMessage({ type: 'GAIA_EMBED_AGENT_DONE' }, '*');
    }
    wasRunningRef.current = isRunning;
  }, [isRunning]);


  const handleSelectThread = useCallback(
    (remoteId: string) => {
      runtime.threads.switchToThread(remoteId);
      setView('chat');
    },
    [runtime],
  );

  const handleHeaderThreadSelect = useCallback((_selection: ThreadSelectorSelection) => {
    setView('chat');
  }, []);

  // "Test now" from the Scheduled runs tab — fires the saved prompt as a
  // user message in a fresh chat thread under the *current user's* JWT.
  // This isn't a perfect fidelity test (the scheduler fires under a service
  // token, not a user token), but it's instant feedback for the prompt
  // logic itself, which is what 99% of testing actually wants.
  const handleTestScheduledRun = useCallback(
    (task: { name: string; prompt: string }) => {
      setView('chat');
      runtime.threads.switchToNewThread();
      runtime.thread.append({
        role: 'user',
        content: [{ type: 'text', text: task.prompt }],
      });
      toast.success(`Testing "${task.name}" — running in a new chat`);
    },
    [runtime],
  );

  if (needsSignIn) {
    return <SignInScreen onAuthSuccess={() => {}} />;
  }

  return (
    <NavigationContext.Provider value={{ navigateToDocument }}>
      <div className="flex h-screen flex-col">
        <AppHeader
          view={view}
          onViewChange={setView}
          onThreadSelect={handleHeaderThreadSelect}
        />
        <div className="relative min-h-0 flex-1">
          <div className="absolute inset-0 flex">
            <div className="flex min-w-0 flex-1 flex-col">
              {view === 'home' ? (
                <ConversationsView onSelectThread={handleSelectThread} />
              ) : view === 'inbox' ? (
                <InboxView onSelectThread={handleSelectThread} />
              ) : view === 'documents' ? (
                <DocumentsView
                  initialFilePath={documentPath}
                  onInitialFileConsumed={() => setDocumentPath(null)}
                />
              ) : view === 'settings' ? (
                <SettingsView />
              ) : view === 'scheduledRuns' ? (
                <ScheduledRunsView onTestRun={handleTestScheduledRun} />
              ) : view === 'memory' ? (
                <MemoryView />
              ) : (
                <Thread visibility={visibility} onVisibilityChange={setVisibility} />
              )}
            </div>
            {showAssetPanel ? (
              <div className="flex w-[55%] shrink-0 flex-col border-l">
                <AssetPanel />
              </div>
            ) : view === 'chat' && panelCollapsed ? (
              <button
                onClick={() => setPanelCollapsed(false)}
                className="flex w-6 shrink-0 cursor-pointer items-center justify-center border-l text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Open panel"
                title="Open panel"
              >
                <PanelRightOpen className="size-3.5" />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </NavigationContext.Provider>
  );
}

function AppInner() {
  return (
    <RuntimeProvider>
      <AppContent />
    </RuntimeProvider>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,       // 30s before background refetch
      gcTime: 5 * 60_000,      // 5min cache retention
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  const { userName, googleUser } = useAuth();
  const displayName = googleUser?.name ?? userName;
  const firstName = displayName?.split(' ')[0] ?? null;

  return (
    <QueryClientProvider client={queryClient}>
      <UserNameProvider name={firstName}>
        <Toaster position="bottom-right" />
        <TooltipProvider delayDuration={0}>
          <ErrorBoundary>
            <AppInner />
          </ErrorBoundary>
        </TooltipProvider>
      </UserNameProvider>
    </QueryClientProvider>
  );
}
